"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSetAtom } from "jotai";
import { RESET } from "jotai/utils";
import { routing } from "@/i18n/navigation";
import { useBookingKey } from "@/hooks/booking-checkout/useBookingKey";
import {
  hotelCheckoutPayloadAtom,
  hotelRoomSelectionAtom,
} from "@/stores/hotel-checkout";
import {
  rentalAppliedCouponAtom,
  rentalCheckoutPayloadAtom,
} from "@/stores/rental-checkout";
import { CheckoutPayload } from "@/hooks/booking-checkout/booking.checkout.helpers";

const CHECKOUT_FLOW_PATH_PREFIXES = [
  "/hotels/checkout/",
  "/rentals/checkout/",
] as const;
const CHECKOUT_FLOW_EXACT_PATHS = [
  "/booking/payment",
  "/booking/success",
  "/booking/processing",
] as const;
const LEGACY_BOOKING_SESSION_KEY = "tovtrip_hotel_booking_session";

/**
 * Strip the locale prefix from the pathname.
 * @param pathname
 */
const stripLocalePrefix = (pathname: string) => {
  const segments = pathname.split("/");
  const localeSegment = segments[1];
  const hasLocalePrefix = routing.locales.includes(localeSegment as "en");

  if (!hasLocalePrefix) {
    return pathname;
  }

  const pathWithoutLocale = `/${segments.slice(2).join("/")}`;
  return pathWithoutLocale || "/";
};

/**
 * Check if the current pathname is part of the checkout flow.
 * @param pathname
 */
const isCheckoutFlowPath = (pathname: string) => {
  if (
    CHECKOUT_FLOW_EXACT_PATHS.includes(
      pathname as
      | "/booking/payment"
      | "/booking/success"
      | "/booking/processing",
    )
  ) {
    return true;
  }

  return CHECKOUT_FLOW_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
};

/**
 * Check whether a path belongs to the active hotel booking-key flow.
 * @param pathname
 * @param hotelId
 * @returns
 */
const isHotelBookingKeyFlowPath = (pathname: string, hotelId: string) =>
  pathname === `/hotels/${hotelId}` ||
  pathname === `/hotels/checkout/${hotelId}` ||
  pathname === "/booking/payment";

/**
 * Check whether a path belongs to the hotel room-selection flow.
 * @param pathname
 * @returns
 */
const isHotelRoomSelectionFlowPath = (pathname: string) =>
  /^\/hotels\/(?!checkout\/|search$|map$)[^/]+$/.test(pathname) ||
  pathname.startsWith("/hotels/checkout/") ||
  pathname === "/booking/payment";

/**
 * Check whether a path belongs to the active rental booking-key flow.
 * @param pathname
 * @param rentalId
 * @returns
 */
const isRentalBookingKeyFlowPath = (pathname: string, rentalId: string) =>
  pathname === `/rentals/${rentalId}` ||
  pathname === `/rentals/checkout/${rentalId}` ||
  pathname === "/booking/payment";

/**
 * Component for resetting the checkout flow session when the user navigates away from the checkout page.
 */
export const CheckoutFlowSessionReset = () => {
  const pathname = usePathname();
  const clearCheckoutPayload = useSetAtom(hotelCheckoutPayloadAtom);
  const clearRentalCheckoutPayload = useSetAtom(rentalCheckoutPayloadAtom);
  const clearRentalCoupon = useSetAtom(rentalAppliedCouponAtom);
  const clearRoomSelection = useSetAtom(hotelRoomSelectionAtom);
  const { bookingKey, clearKey } = useBookingKey("hotel");
  const { bookingKey: rentalBookingKey, clearKey: clearRentalBookingKey } = useBookingKey("rental");
  const previousPathRef = useRef<string | null>(null);

  // Remove legacy token fields from the persisted checkout payload on mount.
  useEffect(() => {
    sessionStorage.removeItem(LEGACY_BOOKING_SESSION_KEY);
    clearCheckoutPayload((previous: any) => {
      if (!previous) {
        return previous;
      }

      const sanitized = { ...previous } as CheckoutPayload &
        Record<string, unknown>;
      delete sanitized.bookingSessionKey;
      delete sanitized.bookingToken;
      return sanitized;
    });
  }, [clearCheckoutPayload]);

  // Reset checkout payload when the user navigates away from the checkout flow.
  useEffect(() => {
    const currentPath = stripLocalePrefix(pathname);
    const previousPath = previousPathRef.current;

    if (
      previousPath &&
      isCheckoutFlowPath(previousPath) &&
      !isCheckoutFlowPath(currentPath)
    ) {
      clearCheckoutPayload(RESET);
      clearRentalCheckoutPayload(RESET);
      clearRentalCoupon(RESET);
    }

    previousPathRef.current = currentPath;
  }, [
    clearCheckoutPayload,
    clearRentalCheckoutPayload,
    clearRentalCoupon,
    pathname,
  ]);

  // Reset booking key when the user navigates away from the checkout flow.
  useEffect(() => {
    if (
      bookingKey &&
      !isHotelBookingKeyFlowPath(
        stripLocalePrefix(pathname),
        bookingKey.recordId,
      )
    ) {
      clearKey();
    }
  }, [bookingKey, clearKey, pathname]);

  // Reset the rental key when the user leaves its detail/checkout flow.
  useEffect(() => {
    if (
      rentalBookingKey &&
      !isRentalBookingKeyFlowPath(
        stripLocalePrefix(pathname),
        rentalBookingKey.recordId,
      )
    ) {
      clearRentalBookingKey();
    }
  }, [clearRentalBookingKey, pathname, rentalBookingKey]);

  // Reset room selection when the user leaves the hotel booking flow.
  useEffect(() => {
    if (!isHotelRoomSelectionFlowPath(stripLocalePrefix(pathname))) {
      clearRoomSelection(RESET);
    }
  }, [clearRoomSelection, pathname]);

  return null;
};
